import { Inject, Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { RestService } from '../../services/rest/rest.service';
import type { Observable } from 'rxjs';

/**
 * The include Service
 */
@Injectable({
  providedIn: 'root'
})
export class IncludeService {
  private static readonly ABSOLUTE_URL_PATTERN: RegExp = /^((http|https|ftp):\/\/)/;
  private static readonly DEFAULT_IMG_HEIGHT: string = '420';
  private static readonly DEFAULT_IMAGE_WIDTH: string = '580';

  constructor(@Inject(RestService) private restService: RestService,
              @Inject(DomSanitizer) private domSanitizer: DomSanitizer) {
  }

  /**
   * Get raw static HTML content from input url using rest service
   *
   * @param url url of the page
   * @returns Returns GET response in text
   */
  getHtml(url: string): Observable<string> {
    const restResourceService = this.restService.getResource(url);
    return restResourceService.get({ responseType: 'text' });
  }

  /**
   * This function is used to resolve foreign image tag.
   *
   * @param htmlAsString the raw HTML content
   * @param baseUrl the path that relative image url based on
   * @param addDefaultWidthAndHeight optional boolean indicates using default width and height or not
   * @param imgHeight optional customized image height
   * @param imgWidth optional customized image width
   * @returns Returns HTML content with parsed image tag
   */
  resolveForeignImgTags(htmlAsString: string,
                        baseUrl: string,
                        addDefaultWidthAndHeight?: boolean,
                        imgHeight?: string,
                        imgWidth?: string): string {
    if (htmlAsString === undefined || htmlAsString === null) {
      htmlAsString = '';
    } else {
      htmlAsString = htmlAsString.trim();
    }
    const doc = document.implementation.createHTMLDocument('');
    doc.body.innerHTML = this.domSanitizer.sanitize(SecurityContext.HTML, htmlAsString);
    const images = doc.getElementsByTagName('img');

    for (let i = 0; i < images.length; i++) {
      const image = images[i];
      const imgSrc = image.getAttribute('src');

      if (!IncludeService.ABSOLUTE_URL_PATTERN.test(imgSrc)) {
        image.setAttribute('src', baseUrl + '/' + imgSrc);
      }
      if (addDefaultWidthAndHeight && (image.getAttribute('height') !== undefined || image.getAttribute('width') !== undefined)) {
        const height = (imgHeight === undefined) ? IncludeService.DEFAULT_IMG_HEIGHT : imgHeight;
        const width = (imgWidth === undefined) ? IncludeService.DEFAULT_IMAGE_WIDTH : imgWidth;
        image.setAttribute('height', height);
        image.setAttribute('width', width);
      }
    }

    return doc.body.innerHTML;
  }
}
